import { defineStore } from 'pinia'
import { usePlanetStore } from '@/stores/planetStore.ts'
import type PlayerCreditBalanceSheet from '@/types/PlayerCreditBalanceSheet.ts'
import { useGameStore } from '@/stores/gameStore.ts'
import { useAssetStore } from '@/stores/assetStore.ts'
import { usePlayerOreStore } from '@/stores/playerOreStore.ts'
import { useShipStore } from '@/stores/shipStore.ts'
import { useSpyStore } from '@/stores/spyStore.ts'

export const usePlayerCreditStore = defineStore('playerCreditStore', {
    state: () => ({
        playerCreditBalanceSheet: {
            planets: 0,
            ministers: 0,
            oreTrades: 0,
            shipMaintenance: 0,
            spyCosts: 0,
            commandHierarchyCost: 0,
            creditsPerTurn: 0
        } as PlayerCreditBalanceSheet,
        balanceSheetDetails: 'planets' as string,
    }),
    getters: {

    },
    actions: {
        calculateCreditsPerTurn() {
            const planetStore = usePlanetStore();
            const gameStore = useGameStore();
            const assetStore = useAssetStore();
            const playerOreStore = usePlayerOreStore();
            const shipStore = useShipStore();
            const spyStore = useSpyStore();

            // reset the balance sheet
            this.playerCreditBalanceSheet = {
                planets: 0,
                ministers: 0,
                oreTrades: 0,
                shipMaintenance: 0,
                spyCosts:0,
                commandHierarchyCost: 0,
                creditsPerTurn: 0
            }

            // Calculate credits from planets
            this.playerCreditBalanceSheet.planets = planetStore.getTotalCreditOutput()

            // Calculate debits from minister salaries
            for(const minister of gameStore.playerMinisters.values()) {
                if(minister.fired === 0) {
                    this.playerCreditBalanceSheet.ministers -= assetStore.findMinisterSalaryByCode(minister.code)
                }
            }

            // Calculate debits from ore trades
            for(const trade of playerOreStore.tradeData.values()) {
                const ore = assetStore.findOreById(trade.fromOreId)
                this.playerCreditBalanceSheet.oreTrades += ore.tradeConversionCost * trade.quantity
            }
            // Round up
            this.playerCreditBalanceSheet.oreTrades = -Math.ceil(this.playerCreditBalanceSheet.oreTrades)

            // Calculate debits from ship maintenance
            for(const ship of shipStore.ships.values()) {
                this.playerCreditBalanceSheet.shipMaintenance -= ship.maintenanceCost
            }

            // Round down the ship maintenance
            this.playerCreditBalanceSheet.shipMaintenance = Math.floor(this.playerCreditBalanceSheet.shipMaintenance)

            // Calculate debits from spy costs
            for(const spy of spyStore.spies.values()) {
                if(spy.killed === 0) {
                    this.playerCreditBalanceSheet.spyCosts -= spy.cost
                }
            }

            // Calculate command hierarchy cost (formula: max(0, pow(shipCount - 250, 2) * 0.004432))
            const totalShipCount = shipStore.ships.size
            if (totalShipCount > 250) {
                const excessShips = totalShipCount - 250
                this.playerCreditBalanceSheet.commandHierarchyCost = -Math.floor(Math.pow(excessShips, 2) * 0.004432)
            }

            // Add array of all values
            const credits = Object.values(this.playerCreditBalanceSheet).reduce((acc, val) => acc + val, 0)

            this.playerCreditBalanceSheet.creditsPerTurn = credits

            return credits;
        }
    }
})
